import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageSquarePlus, Share2, HeartHandshake, ChevronRight } from 'lucide-react';
import { SharePopover } from './SharePopover';
import { FeedbackPopover } from './FeedbackPopover';

interface DesktopEngagementActionsV2Props {
  onSubscribe?: () => void;
  isSubscribed?: boolean;
  monthlyPrice?: string;
}

export const DesktopEngagementActionsV2: React.FC<DesktopEngagementActionsV2Props> = ({
  onSubscribe,
  isSubscribed = false,
  monthlyPrice = '$4.99',
}) => {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [activePopover, setActivePopover] = useState<'share' | 'feedback' | null>(null);

  // Refs for button anchoring
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const feedbackButtonRef = useRef<HTMLButtonElement>(null);

  const handleShareClick = () => {
    if (activePopover === 'share') {
      setActivePopover(null);
    } else {
      setActivePopover('share');
    }
  };

  const handleFeedbackClick = () => {
    if (activePopover === 'feedback') {
      setActivePopover(null);
    } else {
      setActivePopover('feedback');
    }
  };

  const actions = [
    {
      id: 'subscribe',
      ref: null,
      icon: isSubscribed ? HeartHandshake : Heart,
      label: isSubscribed ? '¡Gracias!' : 'Apoyar',
      sublabel: isSubscribed ? 'Ya eres suscriptor' : '',
      fullTooltip: isSubscribed
        ? 'Gracias por apoyar Caramba!'
        : 'Apoya con 5 mil/mes para difundir la aplicación',
      onClick: onSubscribe,
      isActive: isSubscribed || false,
      color: 'purple',
      isPrimary: true,
    },
    {
      id: 'feedback',
      ref: feedbackButtonRef,
      icon: MessageSquarePlus,
      label: 'Feedback',
      sublabel: 'Enviar sugerencia',
      fullTooltip: 'Envíanos tus comentarios y sugerencias para mejorar',
      onClick: handleFeedbackClick,
      isActive: activePopover === 'feedback',
      color: 'gray',
    },
    {
      id: 'share',
      ref: shareButtonRef,
      icon: Share2,
      label: 'Compartir',
      sublabel: null,
      fullTooltip: 'Comparte con tus contactos',
      onClick: handleShareClick,
      isActive: activePopover === 'share',
      color: 'gray',
    },
  ];

  const getActionColor = (color: string, isActive: boolean) => {
    const colors: Record<string, { active: string; inactive: string; hover: string }> = {
      purple: {
        active: 'text-white bg-gradient-to-r from-purple-600 to-purple-700',
        inactive: 'text-gray-700 hover:text-purple-600 bg-white',
        hover: 'hover:bg-purple-50',
      },
      blue: {
        active: 'text-white bg-blue-600',
        inactive: 'text-gray-700 hover:text-blue-600 bg-white',
        hover: 'hover:bg-blue-50',
      },
      gray: {
        active: 'text-white bg-gray-700',
        inactive: 'text-gray-700 hover:text-gray-900 bg-white',
        hover: 'hover:bg-gray-50',
      },
    };
    return colors[color] || colors.gray;
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="fixed right-6 z-30"
        style={{
          // Center in content area: header is 80px (h-20), so add 40px to center point
          top: 'calc(50% + 40px)',
          transform: 'translateY(-50%)',
        }}
      >
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200 p-3 space-y-2">
          {actions.map((action) => {
            const Icon = action.icon;
            const colorClass = getActionColor(action.color, action.isActive);
            const isPrimary = action.isPrimary;

            return isPrimary ? (
              // Primary button - purple background with heart icon and floating APOYA badge
              <div key={action.id} className="relative">
                <motion.button
                  ref={action.ref as any}
                  onClick={action.onClick}
                  onMouseEnter={() => !action.isActive && setShowTooltip(action.id)}
                  onMouseLeave={() => setShowTooltip(null)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`
                    relative flex items-center justify-center
                    w-16 h-16 rounded-xl transition-all duration-200 shadow-lg
                    ${action.isActive
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white'
                      : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}
                    border border-purple-200
                  `}
                >
                  <Icon size={20} />

                  <AnimatePresence mode="wait">
                    {showTooltip === action.id && !action.isActive && (
                      <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        className="absolute right-full mr-3 bg-gray-900 text-white rounded-lg shadow-xl z-50"
                        style={{ width: '200px' }}
                      >
                        <div className="p-3">
                          <div className="font-semibold text-sm">{action.label}</div>
                          <div className="text-xs text-gray-400 mt-2">{action.fullTooltip}</div>
                          <div className="mt-3 flex items-center text-xs text-purple-300">
                            <span>Más información</span>
                            <ChevronRight size={12} className="ml-1" />
                          </div>
                        </div>
                        <div className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-gray-900" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>

                {/* APOYA badge outside button container */}
                {!action.isActive && (
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute -top-2 -right-2 bg-purple-600 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow-lg z-10"
                  >
                    APOYA
                  </motion.div>
                )}
              </div>
            ) : (
              // Regular buttons (non-primary)
              <motion.button
                key={action.id}
                ref={action.ref as any}
                onClick={action.onClick}
                onMouseEnter={() => !action.isActive && setShowTooltip(action.id)}
                onMouseLeave={() => setShowTooltip(null)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`
                  relative flex flex-col items-center justify-center
                  w-16 h-16 rounded-xl transition-all duration-200
                  ${action.isActive ? colorClass.active : `${colorClass.inactive} ${colorClass.hover}`}
                  border border-gray-100
                  ${action.isActive ? 'ring-2 ring-offset-2 ring-' + action.color + '-500' : ''}
                `}
              >
                <Icon
                  size={20}
                  className={action.isActive ? 'text-white' : ''}
                />

                <AnimatePresence>
                  {showTooltip === action.id && !action.isActive && (
                    <motion.div
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="absolute right-full mr-3 bg-gray-900 text-white rounded-lg shadow-xl z-50"
                      style={{ width: '200px' }}
                    >
                      <div className="p-3">
                        <div className="font-semibold text-sm">{action.label}</div>
                        {action.sublabel && (
                          <div className="text-xs text-gray-300 mt-1">{action.sublabel}</div>
                        )}
                        <div className="text-xs text-gray-400 mt-2">{action.fullTooltip}</div>
                      </div>
                      <div className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-gray-900" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            );
          })}
        </div>

        {/* Floating notification for successful actions */}
        {/* AnimatePresence removed - was empty placeholder */}
      </motion.div>

      {/* Popovers */}
      <SharePopover
        isOpen={activePopover === 'share'}
        onClose={() => setActivePopover(null)}
        anchorRef={shareButtonRef}
      />

      <FeedbackPopover
        isOpen={activePopover === 'feedback'}
        onClose={() => setActivePopover(null)}
        anchorRef={feedbackButtonRef}
      />
    </>
  );
};